import { Price, PriceInterval, WebPageEvents } from '@griply/types';
import { CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sizes } from 'shared/components/ui/button';
import { currencySignMap } from 'shared/constants';
import { useSubscriptionPrices } from 'shared/hooks/use-subscription-prices';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { isLifetimeDealPrice } from 'shared/utils/is-lifetime-deal-price';

import { LifetimeDealPricingCard } from '../components/lifetime-deal-pricing-card';
import { PlanToggle } from '../components/plan-toggle';
import { PremiumLayout } from '../components/premium-layout';
import { PriceDisplay } from '../components/price-display';
import { PricingCard } from '../components/pricing-card';
import * as Styled from './premium.style';

export type PremiumProps = {
  onSkip: () => void;
  isLoading: boolean;
  onSubmit: (price?: Price) => void;
  initialPriceSelection?: PriceInterval;
};

export const Premium: React.FC<PremiumProps> = ({
  onSkip,
  isLoading,
  onSubmit,
  initialPriceSelection = PriceInterval.Year,
}) => {
  useTrackPageVisitEvent(WebPageEvents.OnboardingPremium, undefined);
  const { t } = useTranslation();

  const { prices } = useSubscriptionPrices();
  const annualPrice = prices.find(
    ({ interval }) => interval === PriceInterval.Year,
  );
  const monthlyPrice = prices.find(
    ({ interval }) => interval === PriceInterval.Month,
  );
  const hasLifetimeDeal = prices.some(isLifetimeDealPrice);

  const [selectedPriceId, setSelectedPriceId] = useState(
    prices.find(({ interval }) => interval === initialPriceSelection)?.id ??
      annualPrice?.id,
  );

  const selectedPrice = prices.find(({ id }) => id === selectedPriceId);
  const isAnnual = selectedPrice?.interval !== PriceInterval.Month;
  const currencySign = selectedPrice
    ? currencySignMap[selectedPrice.currency] ?? selectedPrice.currency
    : '';
  // intentionally ignores discount/coupon — the onboarding paywall shows full price only
  const perMonthCents = selectedPrice
    ? isAnnual
      ? Math.round(selectedPrice.amountInCents / 12)
      : selectedPrice.amountInCents
    : 0;
  const bigPrice = `${currencySign}${(perMonthCents / 100).toFixed(2)}`;
  const caption = isAnnual
    ? t('forms.onboarding.premium.price.per-month-annually', {
        price: `${currencySign}${((annualPrice?.amountInCents ?? 0) / 100).toFixed(2)}`,
      })
    : t('forms.onboarding.premium.price.per-month-monthly');

  const selectAnnual = (annual: boolean) =>
    setSelectedPriceId((annual ? annualPrice : monthlyPrice)?.id);
  const submitSelection = () => onSubmit(selectedPrice);

  const cta = (
    <Styled.CtaButton
      onClick={submitSelection}
      type="button"
      size={Sizes.Large}
      disabled={isLoading || !selectedPrice}
      fullWidth
      alignCenter
      disabledBackgroundOnly
    >
      {isLoading ? (
        <CircularProgress color="primary" size={20} />
      ) : (
        t('dialogs.premium.interactions.confirm.trial')
      )}
    </Styled.CtaButton>
  );

  return (
    <PremiumLayout
      onSkip={onSkip}
      card={
        hasLifetimeDeal ? (
          <LifetimeDealPricingCard
            options={prices}
            selectedOption={selectedPriceId ?? ''}
            onOption={setSelectedPriceId}
            disabled={isLoading}
            cta={cta}
          />
        ) : (
          <PricingCard
            toggle={
              <PlanToggle
                isAnnual={isAnnual}
                onChange={selectAnnual}
                disabled={isLoading}
              />
            }
            price={<PriceDisplay amount={bigPrice} caption={caption} />}
            cta={cta}
          />
        )
      }
    />
  );
};
